import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Slider } from '../../components/ui/slider';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../components/ui/accordion';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import { Alert, AlertDescription } from '../../components/ui/alert';
import api from '../../services/api';
import { 
  Settings, Save, RotateCcw, AlertTriangle, CheckCircle, 
  Sliders, Gauge, TestTube, ShieldAlert, Brain, FileWarning,
  Info, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

// Config type icons
const CONFIG_ICONS = {
  breach_risk: ShieldAlert,
  dpia_risk: FileWarning,
  ai_risk: Brain
};

// Membership bar visualization component
const MembershipBar = ({ memberships, small = false }) => {
  const bars = [
    { key: 'low', label: 'L', color: 'bg-green-500', value: memberships?.low || 0 },
    { key: 'med', label: 'M', color: 'bg-yellow-500', value: memberships?.med || 0 },
    { key: 'high', label: 'H', color: 'bg-orange-500', value: memberships?.high || 0 },
    { key: 'crit', label: 'C', color: 'bg-red-500', value: memberships?.crit || 0 },
  ];

  return (
    <div className={cn("flex gap-1", small ? "h-4" : "h-6")}>
      {bars.map(bar => (
        <div key={bar.key} className="flex flex-col items-center">
          <div 
            className={cn("w-4 rounded-t relative overflow-hidden bg-muted", small ? "h-3" : "h-5")}
          >
            <div 
              className={cn("absolute bottom-0 w-full transition-all", bar.color)}
              style={{ height: `${bar.value * 100}%` }}
            />
          </div>
          <span className="text-[8px] text-muted-foreground">{bar.label}</span>
        </div>
      ))}
    </div>
  );
};

// Risk gauge component
const RiskGauge = ({ score, color, label }) => {
  const rotation = (score / 100) * 180 - 90; // -90 to 90 degrees
  
  return (
    <div className="relative w-32 h-16 mx-auto">
      {/* Gauge background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="w-32 h-32 rounded-full border-8 border-muted" 
             style={{ clipPath: 'polygon(0 0, 100% 0, 100% 50%, 0 50%)' }} />
      </div>
      {/* Colored arc */}
      <div className="absolute inset-0 overflow-hidden">
        <div 
          className="w-32 h-32 rounded-full border-8"
          style={{ 
            borderColor: color,
            clipPath: 'polygon(0 0, 100% 0, 100% 50%, 0 50%)',
            transform: `rotate(${rotation}deg)`,
            transformOrigin: 'center center'
          }} 
        />
      </div>
      {/* Score */}
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 text-center">
        <span className="text-2xl font-bold" style={{ color }}>{score}</span>
        <p className="text-xs text-muted-foreground">{label}</p>
      </div>
    </div>
  );
};

// Variable slider component
const VariableSlider = ({ variable, value, onChange, testResult }) => {
  const factor = testResult?.factors?.find(f => f.variableId === variable.id);
  
  return (
    <div className="space-y-3 p-4 border rounded-lg">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <Label className="font-medium">{variable.name}</Label>
            {variable.inverse && (
              <Badge variant="outline" className="text-xs">Inverse</Badge>
            )}
            <Badge variant="secondary" className="text-xs">
              Weight: {(variable.weight * 100).toFixed(0)}%
            </Badge>
          </div>
          <p className="text-xs text-muted-foreground mt-1">{variable.description}</p>
        </div>
        {factor && <MembershipBar memberships={factor.memberships} small />}
      </div>
      
      <div className="space-y-2">
        <Slider
          value={[value]}
          onValueChange={([v]) => onChange(v)}
          min={0}
          max={100}
          step={1}
          className="w-full"
        />
        <div className="flex justify-between text-xs text-muted-foreground">
          {variable.ticks.map((tick, i) => (
            <span key={i} className="text-center" style={{ width: '20%' }}>{tick}</span>
          ))}
        </div>
      </div>
      
      <div className="flex items-center justify-between text-sm">
        <span>Value: <strong>{value}</strong></span>
        {factor && (
          <span className="text-muted-foreground">
            Contribution: <strong>{factor.weightedContribution.toFixed(2)}</strong>
          </span>
        )}
      </div>
    </div>
  );
};

// Weight editor component
const WeightEditor = ({ variables, onWeightsChange }) => {
  const [weights, setWeights] = useState(
    variables.reduce((acc, v) => ({ ...acc, [v.id]: v.weight * 100 }), {})
  );
  
  const totalWeight = Object.values(weights).reduce((sum, w) => sum + w, 0);
  const isValid = Math.abs(totalWeight - 100) < 0.1;

  const handleWeightChange = (variableId, newWeight) => {
    const updated = { ...weights, [variableId]: newWeight };
    setWeights(updated);
    
    if (Math.abs(Object.values(updated).reduce((s, w) => s + w, 0) - 100) < 0.1) {
      onWeightsChange(updated);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label>Variable Weights</Label>
        <Badge variant={isValid ? "success" : "destructive"}>
          Total: {totalWeight.toFixed(1)}%
        </Badge>
      </div>
      
      {variables.map(variable => (
        <div key={variable.id} className="flex items-center gap-4">
          <span className="text-sm w-40 truncate">{variable.name}</span>
          <Slider
            value={[weights[variable.id]]}
            onValueChange={([v]) => handleWeightChange(variable.id, v)}
            min={0}
            max={100}
            step={1}
            className="flex-1"
          />
          <span className="text-sm w-12 text-right">{weights[variable.id].toFixed(0)}%</span>
        </div>
      ))}
      
      {!isValid && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>Weights must sum to 100%</AlertDescription>
        </Alert>
      )}
    </div>
  );
};

// Threshold editor component
const ThresholdEditor = ({ thresholds, onChange }) => {
  const levels = ['low', 'medium', 'high', 'critical'];
  
  return (
    <div className="space-y-4">
      <Label>Output Thresholds</Label>
      
      {levels.map((level, index) => {
        const threshold = thresholds[level];
        const prevMax = index > 0 ? thresholds[levels[index - 1]].max : 0;
        
        return (
          <div key={level} className="p-3 border rounded-lg space-y-3">
            <div className="flex items-center gap-2">
              <div 
                className="w-4 h-4 rounded-full" 
                style={{ backgroundColor: threshold.color }}
              />
              <span className="font-medium capitalize">{level}</span>
              <span className="text-xs text-muted-foreground">
                ({prevMax} - {threshold.max})
              </span>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Max Score</Label>
                <Input
                  type="number"
                  value={threshold.max}
                  onChange={(e) => onChange(level, 'max', parseFloat(e.target.value))}
                  min={prevMax + 1}
                  max={100}
                />
              </div>
              <div>
                <Label className="text-xs">Color</Label>
                <Input
                  type="color"
                  value={threshold.color}
                  onChange={(e) => onChange(level, 'color', e.target.value)}
                  className="h-9 p-1"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-xs">Label</Label>
              <Input
                value={threshold.label}
                onChange={(e) => onChange(level, 'label', e.target.value)}
              />
            </div>
            
            <div>
              <Label className="text-xs">Recommended Action</Label>
              <Input
                value={threshold.action}
                onChange={(e) => onChange(level, 'action', e.target.value)}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default function RiskSettingsPage() {
  const [configs, setConfigs] = useState({});
  const [activeConfig, setActiveConfig] = useState('breach_risk');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [inputValues, setInputValues] = useState([]);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [testResults, setTestResults] = useState(null);
  const [showTestDialog, setShowTestDialog] = useState(false);

  // Fetch all configurations
  useEffect(() => {
    const fetchConfigs = async () => {
      try {
        setLoading(true);
        const res = await api.get('/risk-settings/configs');
        setConfigs(res.data);
        
        // Initialize input values with defaults
        const config = res.data[activeConfig];
        if (config) {
          setInputValues(config.variables.map(v => v.defaultValue));
        }
      } catch (error) {
        toast.error('Failed to load risk configurations');
      } finally {
        setLoading(false);
      }
    };
    fetchConfigs();
  }, []);

  // Update input values when config changes
  useEffect(() => {
    const config = configs[activeConfig];
    if (config) {
      setInputValues(config.variables.map(v => v.defaultValue));
      setTestResult(null);
    }
  }, [activeConfig, configs]);

  // Run assessment when inputs change
  const runAssessment = useCallback(async () => {
    if (!configs[activeConfig] || inputValues.length === 0) return;
    
    try {
      const res = await api.post('/risk-settings/assess', {
        configId: activeConfig,
        inputValues: inputValues
      });
      setTestResult(res.data);
    } catch (error) {
      console.error('Assessment failed:', error);
    }
  }, [activeConfig, inputValues, configs]);

  useEffect(() => {
    const debounce = setTimeout(runAssessment, 300);
    return () => clearTimeout(debounce);
  }, [runAssessment]);

  // Save configuration
  const saveConfig = async () => {
    try {
      setSaving(true);
      await api.put(`/risk-settings/config/${activeConfig}`, {
        config: configs[activeConfig]
      });
      toast.success('Configuration saved successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  // Reset configuration to defaults
  const resetConfig = async () => {
    try {
      const res = await api.post(`/risk-settings/reset/${activeConfig}`);
      setConfigs(prev => ({
        ...prev,
        [activeConfig]: res.data.config
      }));
      toast.success('Configuration reset to defaults');
      setShowResetDialog(false);
    } catch (error) {
      toast.error('Failed to reset configuration');
    }
  };

  // Run membership tests
  const runTests = async () => {
    try {
      const res = await api.get('/risk-settings/test/membership');
      setTestResults(res.data);
      setShowTestDialog(true);
    } catch (error) {
      toast.error('Failed to run tests');
    }
  };

  // Update variable weight
  const handleWeightsChange = (weights) => {
    setConfigs(prev => {
      const config = prev[activeConfig];
      const updatedVariables = config.variables.map(v => ({
        ...v,
        weight: weights[v.id] / 100
      }));
      return {
        ...prev,
        [activeConfig]: {
          ...config,
          variables: updatedVariables
        }
      };
    });
  };

  // Update threshold
  const handleThresholdChange = (level, field, value) => {
    setConfigs(prev => {
      const config = prev[activeConfig];
      return {
        ...prev,
        [activeConfig]: {
          ...config,
          outputThresholds: {
            ...config.outputThresholds,
            [level]: {
              ...config.outputThresholds[level],
              [field]: value
            }
          }
        }
      };
    });
  };

  // Update input value
  const handleInputChange = (index, value) => {
    setInputValues(prev => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
  };

  const currentConfig = configs[activeConfig];
  const ConfigIcon = CONFIG_ICONS[activeConfig] || Settings;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-6 flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="risk-settings-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Settings className="h-6 w-6" />
              Risk Settings
            </h1>
            <p className="text-muted-foreground">Configure fuzzy logic risk assessment parameters</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={runTests}>
              <TestTube className="h-4 w-4 mr-2" />
              Run Tests
            </Button>
            <Button variant="outline" onClick={() => setShowResetDialog(true)}>
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset
            </Button>
            <Button onClick={saveConfig} disabled={saving}>
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>

        {/* Config Selector */}
        <div className="flex gap-2">
          {Object.entries(configs).map(([id, config]) => {
            const Icon = CONFIG_ICONS[id] || Settings;
            return (
              <Button
                key={id}
                variant={activeConfig === id ? "default" : "outline"}
                onClick={() => setActiveConfig(id)}
                className="flex items-center gap-2"
              >
                <Icon className="h-4 w-4" />
                {config.name.replace(' Assessment', '')}
              </Button>
            );
          })}
        </div>

        {currentConfig && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left: Variables and Sliders */}
            <div className="lg:col-span-2 space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Sliders className="h-5 w-5" />
                    Input Variables
                  </CardTitle>
                  <CardDescription>
                    Adjust sliders to test risk assessment. {currentConfig.variables.length} variables configured.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {currentConfig.variables.map((variable, index) => (
                    <VariableSlider
                      key={variable.id}
                      variable={variable}
                      value={inputValues[index] || variable.defaultValue}
                      onChange={(v) => handleInputChange(index, v)}
                      testResult={testResult}
                    />
                  ))}
                </CardContent>
              </Card>
            </div>

            {/* Right: Result and Configuration */}
            <div className="space-y-6">
              {/* Risk Score Result */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Gauge className="h-5 w-5" />
                    Risk Score
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {testResult ? (
                    <div className="space-y-4">
                      <div className="text-center">
                        <div 
                          className="text-5xl font-bold"
                          style={{ color: testResult.color }}
                        >
                          {testResult.score}
                        </div>
                        <Badge 
                          className="mt-2"
                          style={{ backgroundColor: testResult.color }}
                        >
                          {testResult.label}
                        </Badge>
                      </div>
                      
                      <div className="p-3 bg-muted rounded-lg">
                        <p className="text-xs text-muted-foreground mb-1">Recommended Action:</p>
                        <p className="text-sm font-medium">{testResult.action}</p>
                      </div>

                      {/* Factor breakdown */}
                      <div className="space-y-2">
                        <p className="text-xs text-muted-foreground">Factor Breakdown:</p>
                        {testResult.factors.map((factor) => (
                          <div key={factor.variableId} className="flex items-center justify-between text-sm">
                            <span className="truncate flex-1">{factor.variableName}</span>
                            <MembershipBar memberships={factor.memberships} small />
                            <span className="w-12 text-right font-mono">
                              {factor.weightedContribution.toFixed(1)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      Adjust sliders to see risk assessment
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Configuration Accordion */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Configuration</CardTitle>
                </CardHeader>
                <CardContent>
                  <Accordion type="single" collapsible>
                    <AccordionItem value="weights">
                      <AccordionTrigger className="text-sm">
                        Variable Weights
                      </AccordionTrigger>
                      <AccordionContent>
                        <WeightEditor 
                          variables={currentConfig.variables}
                          onWeightsChange={handleWeightsChange}
                        />
                      </AccordionContent>
                    </AccordionItem>
                    
                    <AccordionItem value="thresholds">
                      <AccordionTrigger className="text-sm">
                        Output Thresholds
                      </AccordionTrigger>
                      <AccordionContent>
                        <ThresholdEditor 
                          thresholds={currentConfig.outputThresholds}
                          onChange={handleThresholdChange}
                        />
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Reset Confirmation Dialog */}
        <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reset Configuration?</DialogTitle>
              <DialogDescription>
                This will reset the {currentConfig?.name} configuration to default values. 
                This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowResetDialog(false)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={resetConfig}>
                Reset to Defaults
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Test Results Dialog */}
        <Dialog open={showTestDialog} onOpenChange={setShowTestDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <TestTube className="h-5 w-5" />
                Membership Function Tests
              </DialogTitle>
              <DialogDescription>
                Verify the fuzzy logic engine is calculating correctly
              </DialogDescription>
            </DialogHeader>
            
            {testResults && (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  {testResults.allPassed ? (
                    <>
                      <CheckCircle className="h-5 w-5 text-green-500" />
                      <span className="text-green-500 font-medium">All tests passed</span>
                    </>
                  ) : (
                    <>
                      <AlertTriangle className="h-5 w-5 text-red-500" />
                      <span className="text-red-500 font-medium">Some tests failed</span>
                    </>
                  )}
                </div>

                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted">
                      <tr>
                        <th className="p-2 text-left">Input</th>
                        <th className="p-2 text-center">LOW</th>
                        <th className="p-2 text-center">MED</th>
                        <th className="p-2 text-center">HIGH</th>
                        <th className="p-2 text-center">CRIT</th>
                        <th className="p-2 text-center">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {testResults.membershipTests.map((test, i) => (
                        <tr key={i} className="border-t">
                          <td className="p-2 font-mono">{test.input}</td>
                          <td className="p-2 text-center font-mono">{test.actual.low}</td>
                          <td className="p-2 text-center font-mono">{test.actual.med}</td>
                          <td className="p-2 text-center font-mono">{test.actual.high}</td>
                          <td className="p-2 text-center font-mono">{test.actual.crit}</td>
                          <td className="p-2 text-center">
                            {test.passed ? (
                              <CheckCircle className="h-4 w-4 text-green-500 mx-auto" />
                            ) : (
                              <AlertTriangle className="h-4 w-4 text-red-500 mx-auto" />
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="p-4 border rounded-lg">
                  <h4 className="font-medium mb-2">Inverse Test</h4>
                  <p className="text-sm text-muted-foreground mb-2">
                    Input: 80 with inverse=true → Effective: {testResults.inverseTest.effectiveValue}
                  </p>
                  <div className="flex items-center gap-4">
                    <span className="text-sm">
                      Expected Centroid: {testResults.inverseTest.expectedCentroid}
                    </span>
                    <span className="text-sm">
                      Actual: {testResults.inverseTest.actualCentroid}
                    </span>
                    {testResults.inverseTest.passed ? (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    ) : (
                      <AlertTriangle className="h-4 w-4 text-red-500" />
                    )}
                  </div>
                </div>
              </div>
            )}
            
            <DialogFooter>
              <Button onClick={() => setShowTestDialog(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
